"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SDTFEngine = void 0;
const sdk_sdtf_primitives_1 = require("@shapediver/sdk.sdtf-primitives");
const sdk_sdtf_v1_1 = require("@shapediver/sdk.sdtf-v1");
const viewer_shared_node_tree_1 = require("@shapediver/viewer.shared.node-tree");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
class SDTFEngine {
    constructor() {
        // #region Properties (4)
        this._logger = viewer_shared_services_1.Logger.instance;
        this._overview = {};
        // #endregion Private Methods (5)
    }
    // #endregion Properties (4)
    // #region Public Static Accessors (1)
    static get instance() {
        return this._instance || (this._instance = new this());
    }
    // #endregion Public Static Accessors (1)
    // #region Public Methods (1)
    /**
     * Load the sdtf content into a scene graph node.
     *
     * @param content the geometry content
     * @returns the scene graph node
     */
    loadContent(content, jwtToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const node = new viewer_shared_node_tree_1.TreeNode("sdtf");
            // We have to be safe and check if the content is a valid SDTF file
            if (!content || (content && !content.href))
                throw new viewer_shared_services_1.ShapeDiverViewerDataProcessingError("SDTFEngine.loadContent: Invalid content was provided to geometry engine.");
            // create the sdtf sdk
            const sdk = jwtToken
                ? yield (0, sdk_sdtf_v1_1.create)({ authToken: jwtToken })
                : yield (0, sdk_sdtf_v1_1.create)();
            // crete the sdtf parser
            const parser = sdk.createParser();
            // parse the file
            this._parsedFile = yield parser.readFromUrl(content.href);
            // reset overview — it will be populated during item loading
            this._overview = {};
            // load all chunks in parallel
            const chunks = yield Promise.all(this._parsedFile.chunks.map((chunk, i) => this.loadChunk(chunk, i)));
            for (const chunk of chunks)
                node.children.push(chunk);
            // create the overview from data collected during item loading
            node.data.push(new viewer_shared_node_tree_1.SDTFOverviewData(this._overview));
            return node;
        });
    }
    // #endregion Public Methods (1)
    // #region Private Methods (5)
    /**
     * Update the overview with a single attribute entry.
     * Called during item loading so we avoid a separate pass over all items.
     */
    updateOverview(key, dataTypehint, value) {
        var _a, _b, _c, _d;
        const overview = this._overview;
        // check if the attribute is already in the overview
        const existingEntries = overview[key]
            ? overview[key].filter((o) => o.typeHint === dataTypehint)
            : [];
        if (overview[key] && existingEntries.length > 0) {
            // update the existing entry
            const entry = existingEntries[0];
            entry.count++;
            if (sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isStringType(dataTypehint)) {
                if (!((_a = entry.values) === null || _a === void 0 ? void 0 : _a.includes(value))) {
                    (_b = entry.values) === null || _b === void 0 ? void 0 : _b.push(value);
                    (_c = entry.countForValue) === null || _c === void 0 ? void 0 : _c.push(1);
                }
                else {
                    const index = (_d = entry.values) === null || _d === void 0 ? void 0 : _d.indexOf(value);
                    if (index !== undefined && index > -1)
                        entry.countForValue[index] += 1;
                }
            }
            if (sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isNumberType(dataTypehint)) {
                entry.min = Math.min(value, entry.min);
                entry.max = Math.max(value, entry.max);
            }
        }
        else {
            if (overview[key]) {
                overview[key].push({
                    typeHint: dataTypehint,
                    count: 1,
                });
            }
            else {
                overview[key] = [
                    {
                        typeHint: dataTypehint,
                        count: 1,
                    },
                ];
            }
            const newEntry = overview[key][overview[key].length - 1];
            if (sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isStringType(dataTypehint)) {
                newEntry.values = [value];
                newEntry.countForValue = [1];
            }
            if (sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isNumberType(dataTypehint)) {
                newEntry.min = value;
                newEntry.max = value;
            }
        }
    }
    /**
     * Load the attributes into a SDTFAttributesData data item.
     *
     * @param attributes
     * @returns
     */
    loadAttributes(attributes) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const data = new viewer_shared_node_tree_1.SDTFAttributesData();
            const keys = Object.keys(attributes.entries);
            // Separate keys into primitive (need content now) and non-primitive (lazy)
            const primitiveKeys = [];
            const lazyKeys = [];
            for (const key of keys) {
                const typeName = (_a = attributes.entries[key].typeHint) === null || _a === void 0 ? void 0 : _a.name;
                if (sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isBooleanType(typeName) ||
                    sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isColorType(typeName) ||
                    sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isNumberType(typeName) ||
                    sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isStringType(typeName)) {
                    primitiveKeys.push(key);
                }
                else {
                    lazyKeys.push(key);
                }
            }
            // Fetch all primitive attribute contents in parallel
            const primitiveContents = yield Promise.all(primitiveKeys.map((key) => attributes.entries[key].getContent()));
            for (let i = 0; i < primitiveKeys.length; i++) {
                const key = primitiveKeys[i];
                const typeHint = attributes.entries[key].typeHint === undefined
                    ? "undefined"
                    : attributes.entries[key].typeHint.name;
                data.attributes[key] = new viewer_shared_node_tree_1.SDTFAttributeData(typeHint, primitiveContents[i]);
                // Update overview inline to avoid a second pass
                this.updateOverview(key, typeHint, primitiveContents[i]);
            }
            // Non-primitive attributes stay lazy
            for (const key of lazyKeys) {
                const typeHint = attributes.entries[key].typeHint === undefined
                    ? "undefined"
                    : attributes.entries[key].typeHint.name;
                data.attributes[key] = new viewer_shared_node_tree_1.SDTFAttributeData(typeHint, () => __awaiter(this, void 0, void 0, function* () {
                    return yield attributes.entries[key].getContent();
                }));
            }
            return data;
        });
    }
    /**
     * Load the chunk into a scene graph node.
     *
     * @param chunk
     * @param chunkId
     * @returns
     */
    loadChunk(chunk, chunkId) {
        return __awaiter(this, void 0, void 0, function* () {
            const chunkDef = new viewer_shared_node_tree_1.TreeNode(chunk.name || "chunk_" + chunkId);
            // if there are attributes, add them to the chunk as data
            if (chunk.attributes !== undefined) {
                chunkDef.data.push(yield this.loadAttributes(chunk.attributes));
            }
            // load items and nodes in parallel
            const [items, nodes] = yield Promise.all([
                chunk.items !== undefined && chunk.items.length > 0
                    ? Promise.all(chunk.items.map((item, i) => this.loadItem(item, i)))
                    : [],
                chunk.nodes !== undefined && chunk.nodes.length > 0
                    ? Promise.all(chunk.nodes.map((n, i) => this.loadNode(n, i)))
                    : [],
            ]);
            if (items.length > 0)
                chunkDef.addChild(items);
            if (nodes.length > 0)
                chunkDef.addChild(nodes);
            return chunkDef;
        });
    }
    /**
     * Load the item into a scene graph node.
     *
     * @param item
     * @param itemId
     * @returns
     */
    loadItem(item, itemId) {
        return __awaiter(this, void 0, void 0, function* () {
            const itemDef = new viewer_shared_node_tree_1.TreeNode(itemId + "");
            // if there are attributes, add them to the item
            let attributes;
            if (item.attributes !== undefined)
                attributes = yield this.loadAttributes(item.attributes);
            // create the typehint
            const typeHint = item.typeHint === undefined ? "undefined" : item.typeHint.name;
            const isPrimitive = sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isBooleanType(typeHint) ||
                sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isColorType(typeHint) ||
                sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isNumberType(typeHint) ||
                sdk_sdtf_primitives_1.SdtfPrimitiveTypeGuard.isStringType(typeHint);
            // For primitive items, fetch content in parallel with attributes (already done above)
            const itemData = isPrimitive
                ? new viewer_shared_node_tree_1.SDTFItemData(typeHint, yield item.getContent(), attributes === null || attributes === void 0 ? void 0 : attributes.attributes)
                : new viewer_shared_node_tree_1.SDTFItemData(typeHint, () => __awaiter(this, void 0, void 0, function* () {
                    return yield item.getContent();
                }), attributes === null || attributes === void 0 ? void 0 : attributes.attributes);
            itemDef.data.push(itemData);
            return itemDef;
        });
    }
    /**
     * Load the node into a scene graph node.
     *
     * @param node
     * @param nodeId
     * @returns
     */
    loadNode(node, nodeId) {
        return __awaiter(this, void 0, void 0, function* () {
            const nodeDef = new viewer_shared_node_tree_1.TreeNode(node.name || "node_" + nodeId);
            // if there are attributes, add them to the node as data
            if (node.attributes !== undefined) {
                nodeDef.data.push(yield this.loadAttributes(node.attributes));
            }
            // load items and nodes in parallel
            const [items, nodes] = yield Promise.all([
                node.items !== undefined && node.items.length > 0
                    ? Promise.all(node.items.map((item, i) => this.loadItem(item, i)))
                    : [],
                node.nodes !== undefined && node.nodes.length > 0
                    ? Promise.all(node.nodes.map((n, i) => this.loadNode(n, i)))
                    : [],
            ]);
            if (items.length > 0)
                nodeDef.addChild(items);
            if (nodes.length > 0)
                nodeDef.addChild(nodes);
            return nodeDef;
        });
    }
}
exports.SDTFEngine = SDTFEngine;
//# sourceMappingURL=SDTFEngine.js.map